import styled from 'styled-components';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  Bar, Line, ComposedChart, CartesianGrid
} from 'recharts';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

export default function RevenueChart(props) {
  const {
    thisYear,
    thisYearMaxPrice,
    selectedMonth
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const monthLabels = [
    t('statistics.chart.month1'), t('statistics.chart.month2'), t('statistics.chart.month3'),
    t('statistics.chart.month4'), t('statistics.chart.month5'), t('statistics.chart.month6'),
    t('statistics.chart.month7'), t('statistics.chart.month8'), t('statistics.chart.month9'),
    t('statistics.chart.month10'), t('statistics.chart.month11'), t('statistics.chart.month12')
  ];

  if (thisYear.length === 0) return null;

  const prevMaxPrice = Math.max(...thisYear.map(m => m.prevTotalPrice));
  const maxPrice = Math.max(thisYearMaxPrice, prevMaxPrice);
  const maximum = maxPrice > 0 ? Math.ceil(maxPrice * 1.15) : 1;

  // recharts용 데이터 변환 //
  const chartData = thisYear.map((month, index) => ({
    name: monthLabels[index],
    index,
    totalPrice: month.totalPrice,
    barHeight: Math.max(month.totalPrice, 0),
    prevTotalPrice: month.prevTotalPrice,
    prevLineHeight: Math.max(month.prevTotalPrice, 0)
  }));

  // 위쪽만 둥근 바 렌더링 //
  function RoundedTopBar({ x, y, width, height, index, fill: overrideFill }) {
    if (!height) return null;
    const fill = overrideFill || (index === selectedMonth ? theme.primary : theme.primaryAlpha20);
    const clipId = `bar-clip-${index}`;
    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect x={x} y={y} width={width} height={height} />
          </clipPath>
        </defs>
        <rect x={x} y={y} width={width} height={height + 7} rx={7} ry={7} fill={fill} clipPath={`url(#${clipId})`} />
      </g>
    );
  }

  // 커스텀 툴팁 //
  function CustomTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const current = data.totalPrice;
    const prev = data.prevTotalPrice;

    if (current === 0 && prev === 0) return null;

    let percent = null;

    if (prev > 0) {
      percent = Math.round(((current - prev) / prev) * 100);
    }

    return (
      <TooltipBox $isDark={isDark}>
        <TooltipMonthLabel $bg={theme.buttonFill}>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {data.name}
          </TooltipText>
        </TooltipMonthLabel>
        {percent !== null && (
          <TooltipText
            fontFamily='NotoSansKR-Medium'
            fontSize={16}
            color={percent >= 0 ? theme.primary : theme.subKey}>
            {(percent >= 0 ? '+' : '') + percent + '%'}
          </TooltipText>
        )}
        <TooltipRow>
          <TooltipColorBox backgroundColor={theme.primary} />
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.primary}>
            {current.toLocaleString('ko-KR')}
          </TooltipText>
        </TooltipRow>
        {prev > 0 && (
          <TooltipRow>
            <TooltipColorBox backgroundColor={theme.subText} />
            <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
              {prev.toLocaleString('ko-KR')}
            </TooltipText>
          </TooltipRow>
        )}
      </TooltipBox>
    );
  }

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
          barCategoryGap="20%">
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={theme.line}
            strokeDasharray="none"
          />
          <XAxis
            dataKey="name"
            axisLine={{ stroke: theme.line }}
            tickLine={false}
            tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 14, fill: theme.text }}
          />
          <YAxis
            domain={[0, maximum]}
            hide={true}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={false}
          />
          <Bar
            dataKey="barHeight"
            activeBar={(props) => <RoundedTopBar {...props} fill={theme.primary} />}
            shape={RoundedTopBar}
          />
          <Line
            type="monotone"
            dataKey="prevLineHeight"
            stroke={theme.subText}
            strokeWidth={2}
            strokeDasharray="6 3"
            dot={{ r: 3, fill: theme.subText, stroke: 'none' }}
            activeDot={{ r: 4, fill: theme.subText, stroke: 'none' }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const TooltipBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  gap: 5px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  white-space: nowrap;
`;

const TooltipMonthLabel = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;

  border-radius: 2px;
  background-color: ${props => props.$bg};
`;

const TooltipRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const TooltipColorBox = styled.div`
  width: 14px;
  height: 14px;

  border-radius: 2px;
  background-color: ${props => props.backgroundColor};
`;

const TooltipText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;